// SPDX-License-Identifier: AGPL-3.0-or-later
//
// parley: the guild soundboard (strand AG, docs/strands/soundboard.md).
// Fluxer has no soundboard, so everything here is parley's own. The
// store follows EntranceSoundLibrary: a MobX singleton that owns the rows
// and the REST calls, keyed by guild because a soundboard belongs to a
// guild, not to a user.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import Guilds from '@app/features/guild/state/Guilds';
import Permission from '@app/features/permissions/state/Permission';
import {http} from '@app/features/platform/transport/RestTransport';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {
	MAX_GUILD_SOUNDBOARD_SOUNDS,
	SOUNDBOARD_SOUND_MAX_DURATION_MS,
	SOUNDBOARD_SOUND_MAX_SIZE,
} from '@fluxer/constants/src/ParleySoundboardConstants';
import {Permissions} from '@fluxer/constants/src/ChannelConstants';
import type {LimitKey} from '@fluxer/constants/src/LimitConfigMetadata';
import {resolveLimit} from '@fluxer/limits/src/LimitResolver';
import {makeAutoObservable, runInAction} from 'mobx';

const logger = new Logger('SoundboardStore');

/** Guild.resolveGuildLimit is private, so the same evaluation lives here. */
function resolveGuildSoundboardLimit(guildId: string | null | undefined, key: LimitKey, fallback: number): number {
	const guild = guildId ? Guilds.getGuild(guildId) : undefined;
	const resolved = resolveLimit(
		RuntimeConfig.limits,
		{traits: new Set<string>(), guildFeatures: new Set(guild?.features ?? [])},
		key,
		{evaluationContext: 'guild'},
	);
	if (!Number.isFinite(resolved) || resolved <= 0) return fallback;
	return Math.floor(resolved);
}

export interface SoundboardSound {
	soundId: string;
	guildId: string;
	name: string;
	emojiName: string | null;
	emojiId: string | null;
	volume: number;
	available: boolean;
	userId: string;
	url: string;
	hash: string;
	contentType: string;
	durationMs: number;
	sizeBytes: number;
}

/** The API row; snake_case, mapped once here like every other store does. */
export interface ApiSoundboardSound {
	sound_id: string;
	guild_id: string;
	name: string;
	emoji_name: string | null;
	emoji_id: string | null;
	volume: number;
	available: boolean;
	user_id: string;
	url: string;
	hash: string;
	extension: string;
	content_type: string;
	duration_ms: number;
	size_bytes: number;
}

export function rowToSound(row: ApiSoundboardSound): SoundboardSound {
	return {
		soundId: row.sound_id,
		guildId: row.guild_id,
		name: row.name,
		emojiName: row.emoji_name ?? null,
		emojiId: row.emoji_id ?? null,
		volume: typeof row.volume === 'number' ? row.volume : 1,
		available: row.available !== false,
		userId: row.user_id,
		url: row.url,
		hash: row.hash,
		contentType: row.content_type,
		durationMs: row.duration_ms,
		sizeBytes: row.size_bytes,
	};
}

export interface SoundboardUpload {
	name: string;
	/** Raw base64 of the audio file, no data-URL prefix. */
	sound: string;
	volume?: number;
	emojiName?: string | null;
	emojiId?: string | null;
}

export interface SoundboardPatch {
	name?: string;
	volume?: number;
	emojiName?: string | null;
	emojiId?: string | null;
}

function patchBody(patch: SoundboardPatch): Record<string, unknown> {
	const body: Record<string, unknown> = {};
	if (patch.name !== undefined) body.name = patch.name;
	if (patch.volume !== undefined) body.volume = patch.volume;
	// The API takes either emoji_name or emoji_id; sending one clears the
	// other, and sending an empty string clears both.
	if (patch.emojiName !== undefined) body.emoji_name = patch.emojiName ?? '';
	if (patch.emojiId !== undefined) body.emoji_id = patch.emojiId ?? '';
	return body;
}

class SoundboardStore {
	/** guild id -> sounds, oldest first (the order `available` follows). */
	sounds: Record<string, Array<SoundboardSound>> = {};
	private loaded = new Set<string>();
	private loading = new Map<string, Promise<void>>();

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}

	forGuild(guildId: string | null | undefined): Array<SoundboardSound> {
		if (!guildId) return [];
		return this.sounds[guildId] ?? [];
	}

	/** What a member may actually play right now. */
	availableForGuild(guildId: string | null | undefined): Array<SoundboardSound> {
		return this.forGuild(guildId).filter((sound) => sound.available);
	}

	/**
	 * The configured cap, evaluated like Guild.maxEmojis does. The API is
	 * authoritative; this is only what the UI shows next to the upload
	 * button and what greys it out.
	 */
	maxSounds(guildId: string | null | undefined): number {
		return resolveGuildSoundboardLimit(guildId, 'max_guild_soundboard_sounds', MAX_GUILD_SOUNDBOARD_SOUNDS);
	}

	maxBytes(guildId: string | null | undefined): number {
		return resolveGuildSoundboardLimit(guildId, 'soundboard_sound_max_size', SOUNDBOARD_SOUND_MAX_SIZE);
	}

	maxDurationMs(guildId: string | null | undefined): number {
		return resolveGuildSoundboardLimit(guildId, 'soundboard_sound_max_duration_ms', SOUNDBOARD_SOUND_MAX_DURATION_MS);
	}

	/**
	 * Adding or editing a sound needs the expression permissions, the same
	 * ones emojis and stickers need; the API enforces the finer rule that
	 * MANAGE_EXPRESSIONS is required to touch someone else's sound.
	 */
	canManage(guildId: string | null | undefined): boolean {
		if (!guildId) return false;
		return (
			Permission.can(Permissions.CREATE_EXPRESSIONS, {guildId}) ||
			Permission.can(Permissions.MANAGE_EXPRESSIONS, {guildId})
		);
	}

	handleSoundsUpdate(guildId: string, rows: ReadonlyArray<ApiSoundboardSound>): void {
		runInAction(() => {
			this.sounds = {...this.sounds, [guildId]: rows.map(rowToSound)};
			this.loaded.add(guildId);
		});
	}

	handleGuildRemoved(guildId: string): void {
		runInAction(() => {
			const next = {...this.sounds};
			delete next[guildId];
			this.sounds = next;
			this.loaded.delete(guildId);
		});
	}

	async load(guildId: string, force = false): Promise<void> {
		const pending = this.loading.get(guildId);
		if (pending) return pending;
		if (this.loaded.has(guildId) && !force) return;
		const request = (async () => {
			try {
				const response = await http.get<Array<ApiSoundboardSound>>(Endpoints.GUILD_SOUNDBOARD_SOUNDS(guildId));
				this.handleSoundsUpdate(guildId, response.body ?? []);
			} catch (error) {
				logger.warn('Failed to load the soundboard', {guildId, error});
			} finally {
				this.loading.delete(guildId);
			}
		})();
		this.loading.set(guildId, request);
		return request;
	}

	async create(guildId: string, upload: SoundboardUpload): Promise<SoundboardSound> {
		const body: Record<string, unknown> = {name: upload.name, sound: upload.sound};
		if (upload.volume !== undefined) body.volume = upload.volume;
		if (upload.emojiName) body.emoji_name = upload.emojiName;
		if (upload.emojiId) body.emoji_id = upload.emojiId;
		const response = await http.post<ApiSoundboardSound>(Endpoints.GUILD_SOUNDBOARD_SOUNDS(guildId), {body});
		const sound = rowToSound(response.body);
		// The gateway sends the authoritative list right after; this only
		// keeps the uploader's own UI from flickering.
		runInAction(() => {
			this.sounds = {...this.sounds, [guildId]: [...this.forGuild(guildId), sound]};
		});
		return sound;
	}

	async update(guildId: string, soundId: string, patch: SoundboardPatch): Promise<SoundboardSound> {
		const response = await http.patch<ApiSoundboardSound>(Endpoints.GUILD_SOUNDBOARD_SOUND(guildId, soundId), {
			body: patchBody(patch),
		});
		const sound = rowToSound(response.body);
		runInAction(() => {
			this.sounds = {
				...this.sounds,
				[guildId]: this.forGuild(guildId).map((entry) => (entry.soundId === soundId ? sound : entry)),
			};
		});
		return sound;
	}

	async remove(guildId: string, soundId: string): Promise<void> {
		await http.delete(Endpoints.GUILD_SOUNDBOARD_SOUND(guildId, soundId));
		runInAction(() => {
			this.sounds = {
				...this.sounds,
				[guildId]: this.forGuild(guildId).filter((entry) => entry.soundId !== soundId),
			};
		});
	}

	/**
	 * Ask the API to play a sound for everyone in the voice channel. The
	 * caller hears it through the same gateway dispatch as everyone else,
	 * so there is no local shortcut to keep in sync.
	 */
	async play(channelId: string, soundId: string): Promise<void> {
		await http.post(Endpoints.VOICE_CHANNEL_SOUNDBOARD_SOUND(channelId), {body: {sound_id: soundId}});
	}
}

export default new SoundboardStore();
